import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Activity, CheckCircle2, AlertCircle, ArrowUpRight } from "lucide-react";

/** Live AI Activity Feed. Polls /api/dashboard/activity every 12s while the
 *  tab is visible, fades each new row in via the .feed-row CSS animation.
 *  No-ops when the document is hidden so we don't burn the LLM key. */
export default function ActivityFeed({ limit = 12 }) {
    const [items, setItems] = useState(null);

    useEffect(() => {
        let mounted = true;
        const fetchOnce = async () => {
            try {
                const { data } = await api.get("/dashboard/activity", { params: { limit } });
                if (mounted) setItems(data);
            } catch {
                if (mounted) setItems([]);
            }
        };
        fetchOnce();
        const tick = () => { if (!document.hidden) fetchOnce(); };
        const id = setInterval(tick, 12000);
        const onVis = () => { if (!document.hidden) fetchOnce(); };
        document.addEventListener("visibilitychange", onVis);
        return () => {
            mounted = false;
            clearInterval(id);
            document.removeEventListener("visibilitychange", onVis);
        };
    }, [limit]);

    if (items === null) {
        return <div className="text-sm text-zinc-500 py-6">Loading activity…</div>;
    }
    if (items.length === 0) {
        return (
            <div className="text-sm text-zinc-500 text-center py-10 border border-dashed border-zinc-900 rounded-sm">
                No agent runs yet. Start a project to see live activity.
            </div>
        );
    }

    return (
        <ul className="space-y-1.5" data-testid="activity-feed">
            {items.map((it) => (
                <li key={it.id} className="feed-row" data-testid={`activity-row-${it.id}`}>
                    <Link
                        to={it.project_id ? `/projects/${it.project_id}` : "#"}
                        className="flex items-center gap-3 px-3 py-2 border border-zinc-900 hover:border-zinc-700 rounded-sm hover:bg-zinc-950 transition-colors"
                    >
                        <StatusIcon status={it.status} />
                        <div className="flex-1 min-w-0">
                            <div className="text-sm text-white truncate">
                                <span className="font-medium">{prettyAgent(it.agent)}</span>
                                <span className="text-zinc-500"> · </span>
                                <span className="text-zinc-300">{it.project_title || it.project_id?.slice(0, 8)}</span>
                            </div>
                            <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-600 mt-0.5">
                                {it.stage?.replaceAll("_", " ")} · {timeAgo(it.timestamp)} · {it.model?.split("-").slice(1, 3).join(" ") || it.model}
                            </div>
                        </div>
                        <ArrowUpRight className="w-3.5 h-3.5 text-zinc-700" />
                    </Link>
                </li>
            ))}
        </ul>
    );
}

function StatusIcon({ status }) {
    if (status === "completed" || status === "ok") {
        return <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />;
    }
    if (status === "failed" || status === "error") {
        return <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />;
    }
    return <Activity className="w-4 h-4 text-amber-400 shrink-0" />;
}

function prettyAgent(a) {
    if (!a) return "Agent";
    return a.replaceAll("_", " ").replace(/\b\w/g, c => c.toUpperCase());
}

function timeAgo(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
}
